import React, { useState } from "react";
import styles from "../styles.module.scss";
import VisitsSection from "./VisitsSection";
import { formatPrice } from "../utils/formatters";
import { ProgressWrapper } from "./ProgressWrapper";
import { calculateProgressNew, parseDateTime } from "../utils/progressCalculations";
import { useStorePricing } from "../../../../utils/useStorePricing";
import notificationIcon from "../../../SVGcomponents/notification-icon.svg";
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import { useTimezoneStore } from '../../../../store/timezoneStore';

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

// Иконки раскрытия — как в клиентском KeywordSection
const OpenIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="21"
    height="20"
    viewBox="0 0 21 20"
    fill="none"
  >
    <g clipPath="url(#clip0_admin_open)">
      <path
        d="M10.5 2.5C14.6421 2.5 18 5.85786 18 10C18 14.1421 14.6421 17.5 10.5 17.5C6.35786 17.5 3 14.1421 3 10C3 5.85786 6.35786 2.5 10.5 2.5Z"
        stroke="var(--ui-text-primary-active)"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M7.375 11.25L10.5 8.125L13.625 11.25"
        stroke="var(--ui-text-primary-active)"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
    <defs>
      <clipPath id="clip0_admin_open">
        <rect
          width="20"
          height="20"
          fill="var(--ui-card-bg-default)"
          transform="matrix(1 0 0 -1 0.5 20)"
        />
      </clipPath>
    </defs>
  </svg>
);

const ClosedIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="21"
    height="20"
    viewBox="0 0 21 20"
    fill="none"
  >
    <g clipPath="url(#clip0_admin_closed)">
      <path
        d="M10.5 17.5C14.6421 17.5 18 14.1421 18 10C18 5.85786 14.6421 2.5 10.5 2.5C6.35786 2.5 3 5.85786 3 10C3 14.1421 6.35786 17.5 10.5 17.5Z"
        stroke="var(--ui-text-caption-default)"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M7.375 8.75L10.5 11.875L13.625 8.75"
        stroke="var(--ui-text-caption-default)"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
    <defs>
      <clipPath id="clip0_admin_closed">
        <rect width="20" height="20" fill="var(--ui-card-bg-default)" transform="translate(0.5)" />
      </clipPath>
    </defs>
  </svg>
);

interface KeywordSectionProps {
  keyword: {
    keyword: string;
    visits: any[];
    installs: any[];
    totalVisits: {
      current: number;
      target?: number;
      total?: number;
      price: number;
    };
    totalInstalls: {
      current: number;
      target?: number;
      total?: number;
      price: number;
    };
  };
  isLast: boolean;
  store?: string;
  orderPrice?: number;
  keywordsCount?: number;
  orderStatus?: string;
}

const KeywordSection: React.FC<KeywordSectionProps> = ({
  keyword,
  isLast,
  store,
  orderPrice,
  keywordsCount,
  orderStatus,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  let totalKeywordPrice_old = 0;
  const { utcTimezone } = useTimezoneStore()
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  let totalInstallPrice_old = 0;
  let appOpeningDayTwo = false;

  // Старый расчёт прогресса по времени — оставлен как _old,
  // в клиенте прогресс считается по количеству инсталлов
  let totalTimeProgress = 0;
  let progressPoints = 0;

  const { visitPrice, keywordPrice } = useStorePricing(store || "");

  // Get today's date for comparison
  const processDateProgress = (items: any[]) => {
    if (!Array.isArray(items) || items.length === 0) return;

    const today = dayjs().tz(utcTimezone);
    items.forEach(item => {
      const itemDate =  dayjs.tz(`${item.date}`, 'DD.MM.YYYY', utcTimezone);

      if (itemDate.isAfter(today)) {
        progressPoints++;
        return;
      }

      if (itemDate.isBefore(today.startOf('day'))) {
        totalTimeProgress += 100;
        progressPoints++;
        return;
      }

      if (item.startTime && item.endTime) {
        const startDate = dayjs.tz(`${item.date} ${item.startTime}`, 'DD.MM.YYYY HH:mm', utcTimezone);
        const endDate =  dayjs.tz(`${item.date} ${item.endTime}`, 'DD.MM.YYYY HH:mm', utcTimezone);

        const nativeStartDate = startDate.toDate();
        const nativeEndDate = endDate.toDate();

        const timeProgress = calculateProgressNew(nativeStartDate, nativeEndDate);
        totalTimeProgress += timeProgress;
        progressPoints++;
      }
    });
  };

  // Process visits and installs to get time-based progress
  processDateProgress(keyword.visits);
  processDateProgress(keyword.installs);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const progress_old = progressPoints > 0 ? Math.round(totalTimeProgress / progressPoints) : 0;

  // Старый (некорректный) расчёт по захардкоженным тарифам useStorePricing —
  // оставлен как _old, реальные цены приходят с бэка
  if (Array.isArray(keyword.visits)) {
    keyword.visits.forEach(visit => {
      const total = Number(visit.total || 0);
      if (total > 0) {
        totalKeywordPrice_old += visitPrice * total;
      }
    });
  }

  if (Array.isArray(keyword.installs)) {
    keyword.installs.forEach(install => {
      const total = Number(install.total || 0);
      if (total > 0) {
        totalKeywordPrice_old += keywordPrice * total;
        totalInstallPrice_old += keywordPrice * total;
      }
      if(install.appOpeningDayTwo) {
        appOpeningDayTwo = true;
      }
    });
  }

  // Как в клиенте: суммы по ключевому слову берутся из бэка,
  // а при единственном ключе показывается цена заказа целиком
  const totalKeywordPrice =
    (keyword.totalInstalls?.price || 0) + (keyword.totalVisits?.price || 0);
  const headerPrice =
    keywordsCount === 1 ? (orderPrice ?? totalKeywordPrice) : totalKeywordPrice;
  const totalInstallPrice = keyword.totalInstalls?.price || 0;

  // Как в клиенте: прогресс и счётчик в шапке — по инсталлам с бэка
  const isLaunching = orderStatus === "LAUNCHING" || orderStatus === "LAUNCH";
  const totalInstallsCount =
    keyword.totalInstalls?.target ?? keyword.totalInstalls?.total ?? 0;
  const currentInstallsCount = keyword.totalInstalls?.current || 0;
  let progress = 0;
  if (!isLaunching && totalInstallsCount > 0) {
    progress = Math.round((currentInstallsCount / totalInstallsCount) * 100);
  }

  // Блок Visits скрыт (в клиенте он не рендерится); для возврата поставить true
  const showVisitsSection = false;
  const visitsSectionBlock_old = showVisitsSection ? (
    <VisitsSection
      title="Visits"
      keyword={keyword.visits || []}
      pricePerUnit={visitPrice}
      store={store}
      totalKeyword={keyword.totalVisits}
    />
  ) : null;

  // Старый блок "App Opennings" в две строки — скрыт, в клиенте вместо него
  // иконка с подсказкой рядом с ценой (цена уже включает App Opening)
  const showAppOpeningsBlock_old = false;
  const appOpeningsBlock_old = showAppOpeningsBlock_old && appOpeningDayTwo ? (
    <div className={styles.appOpeningsViews}>
      <h1>App Opennings</h1>
      <h1>{formatPrice(totalInstallPrice)}</h1>
    </div>
  ) : null;

  return (
    <div style={{ overflow: "visible" }}>
      <div className={styles.keywordDetails}>
        <div className={styles.keywordHeader}>
          <div className={styles.keywordItem}>
            <span className={styles.keywordItemText}>
              {keyword?.keyword || "Unknown Keyword"}
            </span>
          </div>
          <ProgressWrapper
            value={isLaunching ? 0 : progress}
            status={orderStatus || (progress === 100 ? "DONE" : "IN_PROGRESS")}
          />
          <span className={styles.keywordCount}>
            {currentInstallsCount}/{totalInstallsCount}
          </span>
          <span className={styles.keywordTotalPrice}>
            {/* Иконка слева от цены, чтобы правый край цены совпадал
                с колонкой цен в строках по дням */}
            {appOpeningDayTwo && (
              <span
                className={styles.notificationIcon}
                title="Price with App Opening Day 2 included"
              >
                <img src={notificationIcon} alt="app opening info" />
              </span>
            )}
            {formatPrice(headerPrice)}
          </span>
          <button
            type="button"
            className={styles.expandButton}
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? <OpenIcon /> : <ClosedIcon />}
          </button>
        </div>
        {appOpeningsBlock_old}
      </div>

      {/* Блок Visits скрыт (как в клиенте — он там не рендерится), код оставлен как _old */}
      {visitsSectionBlock_old}

      {isExpanded && (
        <VisitsSection
          title="Installs"
          keyword={keyword.installs || []}
          pricePerUnit={keywordPrice}
          store={store}
          totalKeyword={keyword.totalInstalls}
        />
      )}

      {!isLast && <div className={styles.divider}></div>}
    </div>
  );
};

export default KeywordSection;
